import { jwtDecode } from "jwt-decode";
import axios from "axios";
import host from "../global";
import { resetAnalytics } from "./analytics";

const ACCESS_TOKEN_COOKIE = "accessToken";

function getStoredAccessToken() {
  // The real session lives in the HttpOnly accessToken cookie now (set by the
  // backend on login/refresh, sent automatically on same-site requests). This
  // sessionStorage copy exists only for the handful of places that need the
  // raw JWT client-side (role-claim decoding, the sourcing-microservice auth
  // header) -- see tests/api/ and the HttpOnly-cookie migration plan.
  return sessionStorage.getItem(ACCESS_TOKEN_COOKIE) || null;
}

/**
 * Drop-in replacements for the aws-amplify/auth calls this app used to make
 * directly against Cognito from the browser. Tokens now come from the
 * backend's /api/auth/* endpoints instead -- same Cognito JWTs, same
 * "cognito:groups" claim, so every existing call site keeps working by
 * swapping only its import from "aws-amplify/auth" to this file.
 */

export async function fetchAuthSession() {
  const token = getStoredAccessToken();
  if (!token) {
    return { tokens: undefined };
  }
  let payload;
  try {
    payload = jwtDecode(token);
  } catch {
    return { tokens: undefined };
  }
  return {
    tokens: {
      accessToken: {
        toString: () => token,
        payload,
      },
    },
  };
}

export async function getCurrentUser() {
  const token = getStoredAccessToken();
  if (!token) {
    throw new Error("No authenticated user");
  }
  const payload = jwtDecode(token);
  return { userId: payload.sub, username: payload.username || payload.sub };
}

export async function signOut() {
  const token = getStoredAccessToken();
  // Clear everything this app keeps about the signed-in user, so a shared
  // device does not keep the previous user's identifiers after logout.
  ["refreshToken", "accessToken", "userEmail", "username", "org_admin_id", "current_org_id",
   "impersonatedUser", "searchQuery", "filters"].forEach((key) => localStorage.removeItem(key));
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("impersonationToken");
  // ...and in analytics: the next person here starts as a new anonymous visitor
  resetAnalytics();
  try {
    // access_token in the body is optional now -- the backend reads the
    // HttpOnly cookie first and clears it on response (see auth_router.py).
    await axios.post(`${host}/api/auth/logout`, { access_token: token || undefined }, { withCredentials: true });
  } catch (error) {
    // Best-effort -- local session cleanup (done by callers) still proceeds
    console.error("Error calling backend logout:", error);
  }
}
